import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { Edition, Product } from '../types';

import { ANALYTICS_CHANNEL, defaultAttributes, getAttributesFromContexts } from './use-analytics';

export type UIEvent = {
	action: string;
	actionSubject: string;
	actionSubjectId?: string;
	attributes: Record<string, any>;
};

/**
 * This hook is designed to be used before contexts are initialized to avoid circular dependencies.
 */
export function useUIAnalytics({
	product,
	edition,
}: {
	edition: Edition;
	product: Product;
}): ({ action, actionSubject, actionSubjectId, attributes }: UIEvent) => void {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		({ action, actionSubject, actionSubjectId, attributes }: UIEvent) => {
			const event = createAnalyticsEvent({
				eventType: 'ui',
				action,
				actionSubject,
				actionSubjectId,
				attributes: {
					...defaultAttributes,
					product,
					currentProductEdition: edition,
					...attributes,
				},
			});

			const contextAttributes = getAttributesFromContexts(event.context);

			event
				.update({
					attributes: {
						...contextAttributes,
						...event.payload.attributes,
					},
				})
				.fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, product, edition],
	);
}
